/**
 * AbfCard — protections patrimoniales (Architecte des Bâtiments de France).
 *
 * UX :
 *   - Header standard EnvCardShell.
 *   - State "ready & count=0" → bandeau vert calme "Aucune servitude AC
 *     détectée". Pas de stress visuel pour le 80 % de fiches concernées.
 *   - State "ready & count≥1" → bandeau ambre + liste des protections
 *     touchant la parcelle. Chaque ligne : pictogramme catégorie · libellé
 *     court · lien PDF de l'arrêté quand dispo.
 *
 * On surface aussi un mini-paragraphe pédagogique "ce que ça implique"
 * parce que beaucoup d'utilisateurs (acquéreurs particuliers) ne savent
 * pas ce qu'est l'avis ABF — et c'est exactement le point que les fiches
 * d'agence omettent typiquement.
 */

import type { FicheDoc } from "../../lib/fiches";
import { useAbf, type AbfData, type AbfProtection } from "../../lib/env/abf";
import { EnvCardShell } from "./EnvCardShell";
import { Ic } from "../icons";

type Props = { fiche: FicheDoc };

export function AbfCard({ fiche }: Props) {
  const state = useAbf(fiche);
  const subtitle = state.status === "ready" ? summarise(state.data) : undefined;

  return (
    <EnvCardShell
      icon={<Ic.Award size={18} />}
      title="Protections patrimoniales (ABF)"
      subtitle={subtitle}
      state={state}
      footer={
        <>
          Source : Géoportail de l'Urbanisme · Servitudes d'Utilité Publique
          de catégorie AC (abords MH, sites classés/inscrits, sites
          patrimoniaux remarquables).
          {state.status === "ready" && state.data.queryShape === "polygon" && (
            <> Intersection calculée sur la géométrie cadastrale.</>
          )}
        </>
      }
    >
      {state.status === "ready" && <AbfBody data={state.data} />}
    </EnvCardShell>
  );
}

// ─── Subtitle ────────────────────────────────────────────────────────────

function summarise(d: AbfData): string {
  if (d.count === 0) return "Aucune servitude patrimoniale détectée";
  const labels: string[] = [];
  if (d.counts.ac4) labels.push(`${d.counts.ac4} SPR`);
  if (d.counts.ac1) labels.push(`${d.counts.ac1} abord MH`);
  if (d.counts.ac2) labels.push(`${d.counts.ac2} site classé`);
  return `Avis ABF requis · ${labels.join(" · ")}`;
}

// ─── Body ────────────────────────────────────────────────────────────────

function AbfBody({ data }: { data: AbfData }) {
  if (data.count === 0) {
    return (
      <div className="rounded-lg bg-emerald-50 border border-emerald-100 px-3.5 py-3 flex items-start gap-2.5">
        <span className="w-5 h-5 rounded-full bg-emerald-100 text-emerald-700 grid place-items-center shrink-0 mt-0.5">
          <Ic.Check size={12} />
        </span>
        <div>
          <div className="text-[13px] font-medium text-emerald-900">
            Pas de servitude patrimoniale.
          </div>
          <div className="text-[12px] text-emerald-700 mt-0.5 leading-snug">
            La parcelle n'est pas touchée par un périmètre AC1/AC2/AC4 — un
            projet de rénovation ou d'extension n'aura pas à passer par
            l'avis conforme de l'ABF (sauf déclenchement par d'autres
            règles : zone PLU spécifique, lotissement, …).
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Bandeau d'avertissement */}
      <div className="rounded-lg bg-amber-50 border border-amber-100 px-3.5 py-3 flex items-start gap-2.5">
        <span className="w-5 h-5 rounded-full bg-amber-100 text-amber-700 grid place-items-center shrink-0 mt-0.5">
          <Ic.AlertTriangle size={12} />
        </span>
        <div>
          <div className="text-[13px] font-medium text-amber-900">
            Avis de l'Architecte des Bâtiments de France requis.
          </div>
          <div className="text-[12px] text-amber-800 mt-0.5 leading-snug">
            Tout projet modifiant l'aspect extérieur (façade, toiture,
            menuiseries, clôture, panneaux solaires) passe par l'ABF — délais
            réels 1 à 6 mois, refus possibles. Anticiper dans le rétroplanning.
          </div>
        </div>
      </div>

      {/* Liste des protections */}
      <ul className="space-y-1.5">
        {data.items.map((p) => (
          <ProtectionLine key={p.idgen} p={p} />
        ))}
      </ul>
    </div>
  );
}

function ProtectionLine({ p }: { p: AbfProtection }) {
  // Code couleur par sévérité d'usage : SPR (AC4) le plus structurant en
  // amber appuyé, MH (AC1) en amber doux, sites (AC2) en gris-amber neutre.
  const tone =
    p.suptype === "ac4"
      ? "bg-amber-50/80 ring-amber-200 text-amber-900"
      : p.suptype === "ac1"
        ? "bg-amber-50/40 ring-amber-100 text-ink-900"
        : "bg-ink-50 ring-ink-200 text-ink-900";

  const label = p.typeass && p.typeass !== p.category ? p.typeass : p.category;

  return (
    <li
      className={`flex items-start gap-2.5 rounded-lg ring-1 ring-inset ${tone} px-3 py-2`}
    >
      <span className="shrink-0 mt-0.5 text-ink-700">
        <CategoryIcon supt={p.suptype} />
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-[12.5px] font-medium leading-snug">{p.name}</div>
        <div className="text-[11px] text-ink-600 mt-0.5">
          {label}
          {p.fichierUrl && (
            <>
              {" · "}
              <a
                href={p.fichierUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-ink-700 underline decoration-ink-300 hover:text-ink-900"
              >
                arrêté
              </a>
            </>
          )}
        </div>
      </div>
      <span className="shrink-0 text-[10px] font-mono uppercase tracking-wider text-ink-500 mt-0.5">
        {p.suptype}
      </span>
    </li>
  );
}

function CategoryIcon({ supt }: { supt: AbfProtection["suptype"] }) {
  switch (supt) {
    case "ac1":
      return <Ic.Award size={14} />;
    case "ac4":
      return <Ic.Building size={14} />;
    case "ac2":
      return <Ic.Tree size={14} />;
  }
}
